import {Injectable, OnDestroy} from '@angular/core';
import {MediaService} from "../../../services/ocl";
import {BehaviorSubject, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../../../environments/environment";
import notify from "devextreme/ui/notify";

const defaultTimer = 120;

@Injectable({
  providedIn: 'root'
})
export class MicToJsonService implements OnDestroy {
  private area?: string;
  private fileName?: string;
  private file?: Blob;
  private jsonStructure?: object;
  private questions?: string[];
  private isLoading$: Subject<boolean> = new BehaviorSubject<boolean>(false);
  private transcriptionAvailable$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private jsonAvailable$: BehaviorSubject<object> = new BehaviorSubject<object>({});
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(public mediaService: MediaService, private httpClient: HttpClient) {
    this.handleRecording = this.handleRecording.bind(this);
    this.mediaService.configureMicListener(defaultTimer);
    this.mediaService.RecordingAvailable
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(this.handleRecording);
    this.transcriptionAvailable$.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.isLoading$.next(false);
      });
    this.jsonAvailable$.pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.isLoading$.next(false);
      });
  }

  get JsonAvailable(): BehaviorSubject<object> {
    return this.jsonAvailable$;
  }

  get TranscriptionAvailable(): BehaviorSubject<string> {
    return this.transcriptionAvailable$;
  }

  set Area(area: string) {
    this.area = area;
  }

  set FileName(fileName: string) {
    this.fileName = fileName;
  }

  get IsLoading(): Subject<boolean> {
    return this.isLoading$;
  }

  public async handleMicClick() {
    if (!this.mediaService.IsMicListening) {
      await this.mediaService.startRecording();
    } else {
      await this.mediaService.stopRecording();
    }
  }

  get FileName() {
    return this.fileName ? `${this.fileName}-${new Date().toISOString()}.webm` : 'audio.webm';
  }

  get JsonObject(): object {
    return this.jsonStructure ?? {};
  }

  set JsonObject(json: object) {
    this.jsonStructure = json;
  }

  get Questions(): string[] {
    return this.questions ?? [];
  }

  set Questions(questions: string[]) {
    this.questions = questions;
  }

  public async generateJsonFromTranscript(isDocumentGenerationEnabledForArea: boolean, transcript: string): Promise<void> {
    if (this.file == null) {
      alert("No recording available to generate data from");
      this.jsonAvailable$.next(this.JsonObject);
    }

    this.isLoading$.next(true);
    const formData = new FormData();
    formData.append('area', this.area ?? '');
    formData.append('transcript', transcript);
    formData.append('file', this.file as Blob, this.FileName);
    formData.append('json', JSON.stringify(this.JsonObject));
    formData.append('questions', JSON.stringify(this.Questions));
    formData.append('generateDocument', isDocumentGenerationEnabledForArea.toString());

    const response = await this.httpClient.post<string>(`${environment.aiCommands.apiUrl}/transcript-to-json`, formData, {
      headers: {
        'enctype': 'multipart/form-data'
      }
    }).toPromise();

    try {
      const json = JSON.parse(response);
      this.jsonAvailable$?.next(json);
    } catch (e) {
      notify({ message: "There was an error trying to understand your audio, or there are too many requests at this moment. Please try again later.", type: 'error'});
      await this.mediaService.reset();
      this.TranscriptionAvailable.next('');
      this.isLoading$.next(false);
    }
  }

  private async handleRecording(chunks: Blob[]) {
    this.isLoading$.next(true);
    this.file = new Blob(chunks, {type: 'audio/webm'});
    const formData = new FormData();
    formData.append('area', this.area ?? '')
    formData.append('file', this.file, this.FileName);
    const response = await this.httpClient.post<string>(`${environment.aiCommands.apiUrl}/audio-transcript`, formData, {
      headers: {
        'enctype': 'multipart/form-data'
      }
    }).toPromise();
    this.transcriptionAvailable$?.next(response);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
//todo on dialog close stop recording

